'use client';

import { Text } from '@lichtblick/photon/atoms/text/text';
import classNames from 'classnames';
import { type FormHTMLAttributes, type PropsWithChildren, type Ref, useActionState } from 'react';

import styles from '../form-box/form-box.module.scss';

type CheckoutFlowFormBoxProps<ActionState> = Omit<FormHTMLAttributes<HTMLFormElement>, 'action'> &
  PropsWithChildren & {
    action: (state: Awaited<ActionState>, formData: FormData) => Promise<ActionState>;
    ref?: Ref<HTMLFormElement>;
    showActionErrors?: boolean;
    wide?: boolean;
  };

export const CheckoutFlowFormBox = <ActionState,>({
  action,
  children,
  className,
  ref,
  showActionErrors = false,
  wide,
  ...props
}: CheckoutFlowFormBoxProps<ActionState>) => {
  const [actionState, dispatch] = useActionState(action, undefined as Awaited<ActionState>);
  const errors = getActionErrors(showActionErrors ? actionState : undefined);

  return (
    <form
      className={classNames(styles.box, wide && styles['box-wide'], className)}
      {...props}
      action={dispatch}
      ref={ref}
    >
      {errors.length > 0 && (
        <div role="alert">
          {errors.map((message) => (
            <Text key={message} renderAs="p">
              {message}
            </Text>
          ))}
        </div>
      )}
      {children}
    </form>
  );
};

const getActionErrors = (state: unknown): string[] => {
  if (typeof state !== 'object' || state === null || !('success' in state) || state.success !== false) {
    return [];
  }
  if (!('errors' in state) || !Array.isArray(state.errors)) {
    return [];
  }
  return state.errors.flatMap((error) =>
    typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
      ? [error.message]
      : [],
  );
};
